import {
  Badge,
  Button,
  Center,
  Heading,
  HStack,
  Table as ChakraTable,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  VStack,
  useToast,
  IconButton,
  Tooltip,
} from '@chakra-ui/react';
import { Pagination } from '@olagg/ui-kit';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePushNotifications } from './usePushNotifications';
import { PushNotificationsFilter, FilterParams } from './PushNotificationsFilter';
import moment from 'moment';
import { PushNotification } from './types';
import { EditIcon, DeleteIcon } from '@chakra-ui/icons';
import { FaPaperPlane, FaRedo } from 'react-icons/fa';
import { fetcher } from '@olagg/api-hooks';
import { AdminPushNotificationEndpoints } from '@olagg/api-hooks/endpoints/adminEndpoints';

const getStatusColor = (status: string) => {
  const colors: Record<string, string> = {
    draft: 'gray',
    scheduled: 'blue',
    sending: 'yellow',
    sent: 'green',
    failed: 'red',
  };
  return colors[status] || 'gray';
};

const getStatusText = (status: string) => {
  const texts: Record<string, string> = {
    draft: 'Borrador',
    scheduled: 'Programada',
    sending: 'Enviando',
    sent: 'Enviada',
    failed: 'Fallida',
  };
  return texts[status] || status;
};

export const PushNotifications = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const [filter, setFilter] = useState<FilterParams & { page: number; limit: number }>({
    title: searchParams.get('title') || undefined,
    status: searchParams.get('status') || undefined,
    page: Number(searchParams.get('page') || 1),
    limit: Number(searchParams.get('limit') || 25),
  });

  const { pushNotifications, count, isLoading, mutate } = usePushNotifications(filter);

  const handleFilter = (newFilters: FilterParams) => {
    const updatedFilter = { ...filter, ...newFilters, page: 1 };
    setFilter(updatedFilter);

    const params = new URLSearchParams();
    if (updatedFilter.title) params.set('title', updatedFilter.title);
    if (updatedFilter.status) params.set('status', updatedFilter.status);
    params.set('page', '1');
    params.set('limit', String(updatedFilter.limit));
    setSearchParams(params);
  };

  const handlePageChange = (page: number) => {
    const updatedFilter = { ...filter, page: page + 1 };
    setFilter(updatedFilter);
    searchParams.set('page', String(page + 1));
    setSearchParams(searchParams);
  };

  const handleLimitChange = (value: string) => {
    const updatedFilter = { ...filter, limit: Number(value), page: 1 };
    setFilter(updatedFilter);
    searchParams.set('limit', value);
    searchParams.set('page', '1');
    setSearchParams(searchParams);
  };

  const handleSend = async (notification: PushNotification) => {
    if (!notification.canSend) {
      toast({
        title: 'Error',
        description: 'Esta notificación no se puede enviar',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    try {
      await fetcher(AdminPushNotificationEndpoints.send(notification.uid));
      toast({
        title: 'Éxito',
        description: 'Push notification enviada correctamente',
        status: 'success',
        duration: 3000,
      });
      mutate();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Error al enviar la notificación',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleResend = async (notification: PushNotification) => {
    if (!notification.canResend) {
      toast({
        title: 'Error',
        description: 'Esta notificación no se puede reenviar',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    if (!confirm('¿Estás seguro de reenviar esta notificación?')) return;

    try {
      await fetcher(AdminPushNotificationEndpoints.resend(notification.uid));
      toast({
        title: 'Éxito',
        description: 'Push notification reenviada correctamente',
        status: 'success',
        duration: 3000,
      });
      mutate();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Error al reenviar la notificación',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleDelete = async (notification: PushNotification) => {
    if (!notification.canDelete) {
      toast({
        title: 'Error',
        description: 'Esta notificación no se puede eliminar',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    if (!confirm('¿Estás seguro de eliminar esta notificación?')) return;

    try {
      await fetcher(AdminPushNotificationEndpoints.delete(notification.uid));
      toast({
        title: 'Éxito',
        description: 'Push notification eliminada correctamente',
        status: 'success',
        duration: 3000,
      });
      mutate();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Error al eliminar la notificación',
        status: 'error',
        duration: 3000,
      });
    }
  };

  return (
    <Center w={'full'} p={4}>
      <VStack color={'white'} w={'full'} spacing={4}>
        <HStack w={'full'} justifyContent={'space-between'}>
          <Heading my={2}>Push Notifications</Heading>
          <Button
            colorScheme={'purple'}
            onClick={() => navigate('/push-notifications/create')}
          >
            Crear Nueva
          </Button>
        </HStack>

        <PushNotificationsFilter onFilter={handleFilter} />

        <ChakraTable variant="striped" colorScheme="olaggPurple">
          <Thead>
            <Tr>
              <Th color={'white'}>Título</Th>
              <Th color={'white'}>Estado</Th>
              <Th color={'white'}>Target</Th>
              <Th color={'white'}>Programada</Th>
              <Th color={'white'}>Enviada</Th>
              <Th color={'white'}>Stats</Th>
              <Th color={'white'}>Acciones</Th>
            </Tr>
          </Thead>
          <Tbody>
            {isLoading ? (
              <Tr>
                <Td colSpan={7} textAlign={'center'}>
                  Cargando...
                </Td>
              </Tr>
            ) : pushNotifications.length === 0 ? (
              <Tr>
                <Td colSpan={7} textAlign={'center'}>
                  No hay notificaciones
                </Td>
              </Tr>
            ) : (
              pushNotifications.map((notification) => (
                <Tr key={notification.id}>
                  <Td>{notification.title}</Td>
                  <Td>
                    <Badge colorScheme={getStatusColor(notification.status)}>
                      {getStatusText(notification.status)}
                    </Badge>
                  </Td>
                  <Td>
                    {notification.targetType === 'user' ? 'Usuario: ' : 'Segmento: '}
                    {notification.targetName}
                  </Td>
                  <Td>
                    {notification.scheduledAt
                      ? moment(notification.scheduledAt).format('DD/MM/YYYY HH:mm')
                      : '-'}
                  </Td>
                  <Td>
                    {notification.sentAt
                      ? moment(notification.sentAt).format('DD/MM/YYYY HH:mm')
                      : '-'}
                  </Td>
                  <Td>
                    ✅ {notification.sentCount} / ❌ {notification.failedCount}
                  </Td>
                  <Td>
                    <HStack spacing={2}>
                      {notification.canEdit && (
                        <Tooltip label="Editar">
                          <IconButton
                            aria-label="Editar"
                            icon={<EditIcon />}
                            size="sm"
                            onClick={() => navigate(`/push-notifications/${notification.uid}/edit`)}
                          />
                        </Tooltip>
                      )}
                      {notification.canSend && (
                        <Tooltip label="Enviar ahora">
                          <IconButton
                            aria-label="Enviar"
                            icon={<FaPaperPlane />}
                            size="sm"
                            colorScheme="green"
                            onClick={() => handleSend(notification)}
                          />
                        </Tooltip>
                      )}
                      {notification.canResend && (
                        <Tooltip label="Reenviar">
                          <IconButton
                            aria-label="Reenviar"
                            icon={<FaRedo />}
                            size="sm"
                            colorScheme="blue"
                            onClick={() => handleResend(notification)}
                          />
                        </Tooltip>
                      )}
                      {notification.canDelete && (
                        <Tooltip label="Eliminar">
                          <IconButton
                            aria-label="Eliminar"
                            icon={<DeleteIcon />}
                            size="sm"
                            colorScheme="red"
                            onClick={() => handleDelete(notification)}
                          />
                        </Tooltip>
                      )}
                    </HStack>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </ChakraTable>

        <Pagination
          params={{
            limit: filter.limit,
            page: filter.page,
            count,
          }}
          onClick={handlePageChange}
          onLimitChange={handleLimitChange}
        />
      </VStack>
    </Center>
  );
};
